/**
 * ApiKeysView — list and manage API keys for the user's organisation.
 * Requires verified email.
 */

import { useCallback, useEffect, useState } from 'react';
import { EmptyState } from '../../components/EmptyState';
import { useModal } from '../../components/Modal';
import { Spinner } from '../../components/Spinner';
import { useToast } from '../../components/Toast';
import { useAuth } from '../../contexts/AuthContext';
import { apiFetch, getNetworkErrorMessage } from '../../lib/api';

interface ApiKeyRecord {
  id: string;
  prefix: string;
  application_id: string;
  created_at: number;
  app_name: string;
}

export function ApiKeysView() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const { openModal } = useModal();
  const [keys, setKeys] = useState<ApiKeyRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchKeys = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await apiFetch<{ apiKeys: ApiKeyRecord[] }>('/v1/api-keys');
      setKeys(data.apiKeys || []);
    } catch (err) {
      setError(getNetworkErrorMessage(err, 'Failed to load API keys.'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  const handleRevoke = (keyId: string, prefix: string) => {
    openModal({
      title: 'Revoke API Key?',
      description: `Key ${prefix}... will be permanently revoked. Applications using this key will stop working immediately.`,
      confirmText: 'Revoke',
      cancelText: 'Cancel',
      variant: 'destructive',
      onConfirm: async () => {
        try {
          await apiFetch(`/v1/api-keys/${keyId}`, { method: 'DELETE' });
          addToast({ type: 'success', message: 'API key revoked' });
          await fetchKeys();
        } catch (err) {
          addToast({
            type: 'error',
            message: getNetworkErrorMessage(err, 'Failed to revoke key.'),
          });
        }
      },
    });
  };

  const handleRotatePlaceholder = () => {
    addToast({
      type: 'info',
      message:
        'API Key Management Coming Soon: Key rotation will be available in a future update. Create another Application to generate a new key.',
    });
  };

  // Gate sensitive action behind email verification
  if (!user?.emailVerified) {
    return (
      <div className="rounded-2xl border border-yellow-500/20 bg-yellow-500/10 p-8 text-center space-y-4 max-w-2xl mx-auto">
        <p className="text-lg font-semibold text-yellow-400 flex items-center justify-center gap-2">
          <span>⚠️</span> Email Verification Required
        </p>
        <p className="text-sm text-yellow-300 leading-relaxed">
          Verify your email address to manage API keys. Use the resend button in the banner above to
          send a verification link to your inbox.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="xl" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-800 bg-red-950/30 p-4 text-sm text-red-300 max-w-2xl mx-auto">
        {error}
        <button
          onClick={fetchKeys}
          className="ml-3 text-xs text-red-400 underline hover:text-red-300"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Security Warning Box */}
      <div className="rounded-xl border border-slate-900 bg-slate-950/50 p-5 space-y-2">
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">
          🔑 Security & Key Lifecycle
        </h3>
        <p className="text-xs text-slate-500 leading-relaxed">
          API Keys are cryptographically hashed before being stored in our database. We only display
          the prefix of your keys below. If you lose an API Key, you must register a new application
          or rotate the key when the feature is enabled.
        </p>
      </div>

      {keys.length === 0 ? (
        <EmptyState
          icon="🔑"
          title="No API Keys Found"
          description="API keys are generated automatically when you create a new application."
        />
      ) : (
        <div className="space-y-3">
          {keys.map((key) => (
            <div
              key={key.id}
              className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-xl border border-slate-900 bg-slate-950 p-5 hover:border-slate-800 transition"
            >
              <div>
                <p className="font-mono text-sm font-semibold text-white tracking-wide">
                  {key.prefix}...
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  App: <span className="text-slate-400 font-semibold">{key.app_name}</span> ·
                  Created {new Date(key.created_at * 1000).toLocaleDateString()}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleRotatePlaceholder}
                  className="rounded-lg border border-slate-800 bg-slate-900/30 px-3 py-2 text-xs font-medium text-slate-400 hover:bg-slate-900/60"
                >
                  Rotate Key
                </button>
                <button
                  type="button"
                  onClick={() => handleRevoke(key.id, key.prefix)}
                  className="rounded-lg border border-red-950/30 bg-red-950/20 px-3 py-2 text-xs font-semibold text-red-400 hover:bg-red-950/40"
                >
                  Revoke
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* API Key Management Info Block */}
      <div className="rounded-xl border border-slate-900 bg-slate-900/20 p-6 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-white">API Key Management</h3>
          <span className="rounded bg-slate-950 border border-slate-800 px-1.5 py-0.5 text-[8px] uppercase tracking-wider text-slate-500 font-bold">
            Coming Soon
          </span>
        </div>
        <p className="text-xs text-slate-400 leading-relaxed">
          Advanced security features such as programmatic key rotation, key scopes (read-only vs
          write-only), and custom expiry periods are currently in development. If you need a new key
          immediately, please create another application.
        </p>
      </div>
    </div>
  );
}
